import * as React from 'react';
import { Group, Box, Button, Grid, Divider, SelectItem, Title, Text, createStyles } from '@mantine/core';
import { useForm, UseFormReturnType } from '@mantine/form';
import { useScrollIntoView, useSessionStorage } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { openConfirmModal } from '@mantine/modals';

// Contexts
import { ViewingAsContext } from '../../contexts/ViewingAs';

// Hooks
import { useEnumDataSet } from './hooks/useEnumDataSet';
import { useStaticKey } from './hooks/useStaticKey';
import { useDynamicRequired } from './hooks/useDynamicRequired';
import { useDynamicDisabled } from './hooks/useDynamicDisabled';

// i18n
import i18n from '../../i18n';

// Manifest
import { IFormSection, IManifestFieldHydrated } from '../../manifests/types';
import { manifest, questionsWithDependents } from '../../manifests/questionManifest';

// Local Components
import { InterpolatedFormCopy } from './components/InterpolatedFormCopy';
import { DatePickerInput } from './components/DatePickerInput';
import { MonthPickerInput } from './components/MonthPickerInput';
import { NumericFormatInput } from './components/NumericFormatInput';
import { InputValueTable } from './components/InputValueTable';
import { NumberInput } from './components/NumberInput';
import { NumberOrNoAnswerRadioGroup } from './components/NumberOrNoAnswerRadioGroup';
import { ListField } from './components/ListField';
import { PatternNumberInput } from './components/PatternNumberInput';
import { PhoneNumberGroup } from './components/PhoneNumberGroup';
import { Select } from './components/Select';
import { MultiSelect } from './components/MultiSelect';
import { Textarea } from './components/Textarea';
import { TextInput } from './components/TextInput';
import { YearMonthPickerInput } from './components/YearMonthPickerInput';
import { YearPickerInput } from './components/YearPickerInput';
import { PercentageOwnershipInput } from './components/PercentageOwnershipInput';
import { FormStepper } from './components/FormStepper';
import { WageRanger } from './components/WageRanger';
import { YesNoRadio } from './components/YesNoRadio';
import { GridCol } from './components/GridCol';
import { FormSummary } from './components/FormSummary';

// Form Modules
import { PrimaryContactForm } from '../PrimaryContactForm';
import { FinancialsTable } from '../FinancialsTable';
import { ContactsModule } from '../ContactsModule';
import { ConfirmContactsModule } from '../ConfirmContactsModule';
import { DefaultSubForm } from '../DefaultSubForm';

// Helpers
import { sectionsToSteps } from './helpers/sectionHelpers';

// Types
import { IPhoneNumber, ISurveyTargetPeriod, SurveyStatus } from '../../types';
import { FieldProps } from '../../manifests/types';

const componentInitialValueTransforms: Record<string, (val: any) => any> = {
    MonthPickerInput: (val: number) => {
        const date = new Date();

        date.setMonth(val - 1);

        return date;
    },
    YearPickerInput: (val: number) => {
        const date = new Date();

        date.setFullYear(val);

        return date;
    },
    YearMonthPickerInput: (val: number | string) => {
        const date = new Date();
        const stringVal = typeof val === 'number' ? val.toString() : val;

        if (stringVal.length) {
            const [year, month] = stringVal.match(/.{1,4}/g) ?? [];

            if (typeof year !== 'undefined') date.setFullYear(parseInt(year));
            date.setMonth(parseInt(month ?? 1) - 1);
            date.setDate(1);
        }

        return date;
    }
};
const componentOnSubmitValueTransforms: Record<string, (val: any) => any> = {
    MonthPickerInput: (val?: Date) => {
        if (val && typeof val?.getMonth !== 'undefined') {
            return val.getMonth() + 1;
        }

        return val;
    },
    YearPickerInput: (val?: Date) => {
        if (val && typeof val?.getFullYear !== 'undefined') {
            return val.getFullYear();
        }

        return val;
    },
    YearMonthPickerInput: (val: Date) => {
        if (val && typeof val?.getFullYear !== 'undefined') {
            const adjustedMonth = `${val.getMonth() + 1}`;

            return `${val.getFullYear()}${adjustedMonth.padStart(2, '0')}`;
        }

        return val;
    },
    PhoneNumberGroup: (val?: IPhoneNumber) => {
        return {
            number: val?.number ? val?.number.replace(/\(|\)|#| /g, '') : undefined,
            ext: val?.ext
        };
    }
};

const useStyles = createStyles((theme) => ({
    hiddenField: {
        display: 'none'
    },
    formSection: {
        // padding: '1.5rem 0 3rem 0'
        padding: '1.5rem 1.25rem',
        border: `1px solid ${theme.colors.gray[3]}`,
        borderBottomWidth: '3px',
        borderRadius: '6px',
        marginTop: '1.25rem',
        background: '#fff'
    },
    drawerCardStyle: {
        marginLeft: '-1rem',
        marginRight: '-1rem',
        borderRadius: '0',
        borderRight: 'none',
        borderLeft: 'none',
        borderBottomWidth: '1px',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        background: '#f9f9f9'
    },
    noCardStyle: {
        padding: 0,
        border: 'none'
    },
    formInsert: {
        paddingLeft: '1.25rem',
        paddingRight: '1.25rem'
    },
    formInsertCardPadding: {
        paddingLeft: '2.3rem',
        paddingRight: '2.3rem',
        [theme.fn.smallerThan('sm')]: {
            paddingLeft: '1rem',
            paddingRight: '1rem'
        }
    },
    confirmContact: {
        border: 'none',
        borderRadius: 0,
        background: '#fcfdff',
        borderBottom: `1px solid ${theme.colors.gray[3]}`,
        paddingTop: '0.7rem',
        paddingLeft: '1.2rem !important',
        paddingRight: '1.2rem !important'
    },
    noMarginTop: {
        marginTop: '0'
    },
    grid: {
        alignItems: 'flex-end'
    },
    sectionDescription: {
        fontSize: theme.fontSizes.sm,
        whiteSpace: 'pre-line'
    },
    formBox: {
        flex: 1,
        [theme.fn.smallerThan('sm')]: {
            maxWidth: '100vw'
        }
    },
    formBoxWithStepper: {
        maxWidth: 'calc(100% - 9rem)',
        [theme.fn.smallerThan('sm')]: {
            maxWidth: '100%'
        }
    },
    stepperBox: {
        minHeight: '100%',
        borderRight: `1px solid ${theme.colors.gray[3]}`,
        marginTop: '-1.25rem',
        paddingTop: '1.25rem',
        marginBottom: '-1.25rem',
        paddingBottom: '1.25rem',
        marginLeft: '-1.25rem',
        background: theme.colors.gray[0],
        position: 'relative',
        zIndex: 3,
        [theme.fn.smallerThan('sm')]: {
            marginRight: '-1.25rem',
            borderBottom: `1px solid ${theme.colors.gray[3]}`,
            borderRight: 'none',
            padding: '0.5rem',
            marginBottom: '0.25rem'
        }
    },
    gridGroup: {
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        [theme.fn.smallerThan('sm')]: {
            flexDirection: 'column'
        }
    }
}));

const ComponentsWithOwnActionButtons = [
    'CON0000B',
    'CON0000C',
    'CON0000D',
    'FIN0000A',
    'FIN0000B',
    'FIN0000C',
    'FIN0000D',
    'FIN0000E'
];

const FormWrapper: React.FC<{
    isSubForm?: boolean;
    children: React.ReactNode;
    targetRef?: React.LegacyRef<HTMLFormElement>;
}> = ({ isSubForm, targetRef, children }) => {
    return isSubForm ? (
        <Box>{children}</Box>
    ) : (
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()} noValidate ref={targetRef}>
            {children}
        </form>
    );
};

export interface FormScaffoldingProps {
    sections: IFormSection<IManifestFieldHydrated>[];
    controlledDataSets?: Record<string, SelectItem[]>;
    ancillaryData?: Record<string, any>;
    data?: Record<string, any>;
    targetPeriod?: ISurveyTargetPeriod;
    onSave?: ({ values, furthestStep }: { values: Record<string, any>; furthestStep: number }) => Promise<any>;
    onComplete?: () => Promise<any>;
    onCancel?: (form: UseFormReturnType<Record<string, any>>) => void;
    progressiveForm?: boolean;
    editMode?: boolean;
    drawerCard?: boolean;
    noCardStyle?: boolean;
    confirmContact?: boolean;
    dirtySaveOnly?: boolean;
    formInsert?: boolean;
    showReviewStep?: boolean;
    disableValidation?: boolean;
    preventStepProgression?: boolean;
    hideCancel?: boolean;
    hideDefaultBack?: boolean;
    parentPrev?: () => void;
    saveLabelKey?: string;
    leftActionButtons?: React.ReactNode;
    isSubForm?: boolean;
    reviewAnswers?: boolean;
    reviewOnly?: boolean;
    onHighlightQuestion?: (qid: string) => void;
    highlightedQuestions?: string[];
    formId?: string;
    clientId?: string;
    furthestStep?: number;
    trigger?: string;
    latestNoteId?: string;
    surveyStatus?: SurveyStatus;
    externalIsLoading?: boolean;
}

export type FormHandle = {
    isDirty: () => boolean;
    reset: () => void;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    isLoading: () => boolean;
};

const isNumericValue = (config: IManifestFieldHydrated): boolean =>
    config?.component === 'NumberOrNoAnswerRadioGroup' ||
    config?.component === 'NumberInput' ||
    (Object.hasOwn(config?.componentProps ?? {}, 'type') && config?.componentProps?.type === 'number');

const getFieldId = (name: string) => `field-${name}`;

export const FormScaffolding = React.forwardRef<FormHandle, FormScaffoldingProps>(
    (
        {
            sections,
            data = {},
            targetPeriod,
            controlledDataSets = {},
            ancillaryData = {},
            onSave,
            onComplete,
            onCancel,
            progressiveForm,
            leftActionButtons,
            drawerCard,
            noCardStyle,
            confirmContact,
            formInsert,
            showReviewStep,
            saveLabelKey,
            parentPrev,
            formId,
            clientId,
            editMode = true,
            dirtySaveOnly = true,
            disableValidation = false,
            preventStepProgression = false,
            hideCancel,
            hideDefaultBack,
            isSubForm = false,
            reviewAnswers = false,
            reviewOnly = false,
            onHighlightQuestion,
            highlightedQuestions,
            furthestStep = 0,
            trigger,
            latestNoteId,
            surveyStatus,
            externalIsLoading,
            ...props
        },
        ref
    ) => {
        const { classes, cx } = useStyles();
        const { userType } = React.useContext(ViewingAsContext);
        const { getStaticKeyByName, getNameByStaticKey, convertMapFromNamesToKeys } = useStaticKey();
        const { determineRequired } = useDynamicRequired();
        const { determineDisabled } = useDynamicDisabled();
        const [changedFieldWithDependents, setChangedFieldWithDependents] = React.useState(false);
        const [activeStep, _setActiveStep, removeActive] = useSessionStorage({
            key: `active-step-${formId}`,
            defaultValue: furthestStep
        });
        const [highlightState, setHightlightState, removeHighlightState] = useSessionStorage<string[] | null>({
            key: `highlight-questions-survey-${formId}-note-${latestNoteId}`,
            defaultValue: null
        });

        const setActiveStep = (val: number | ((prevState: number) => number)): void => {
            _setActiveStep(val);
            form.resetDirty();
            setChangedFieldWithDependents(false);
        };

        React.useEffect(() => {
            if (latestNoteId && highlightState === null && highlightedQuestions && highlightedQuestions?.length) {
                setHightlightState(highlightedQuestions);
            }
        }, [highlightedQuestions, setHightlightState, highlightState, latestNoteId]);
        const [isLoading, setIsLoading] = React.useState(false);
        const { t, i18n: i18next } = useTranslation();
        const { getTranslatedDataSet } = useEnumDataSet();
        const { scrollIntoView, targetRef } = useScrollIntoView<HTMLFormElement>({
            duration: 400,
            offset: 150
        });
        const canSubmit = React.useMemo(() => furthestStep === sections.length, [furthestStep]);
        const initialValues = React.useMemo(() => {
            return sections.reduce((acc, { manifest = [] }) => {
                let accCopy = { ...acc };

                manifest.forEach((item) => {
                    if (!Object.keys(item).length) return accCopy;
                    if ((!item.isValueTable && typeof data?.[item.id] === 'undefined') || data?.[item.id] === null) {
                        accCopy[item.key] = item.defaultValue;
                    } else if (isNumericValue(item)) {
                        accCopy[item.key] = Number(data[item.id]);
                    } else if (typeof componentInitialValueTransforms?.[item.component] !== 'undefined') {
                        accCopy[item.key] = componentInitialValueTransforms[item.component](data[item.id]);
                    } else if (item.isModule && item.isValueTable) {
                        const childrenValueMap = (item?.subManifest ?? []).reduce(
                            (dataObj, child) => ({
                                ...dataObj,
                                [getNameByStaticKey(child.id)]:
                                    data[child.id] ??
                                    (typeof child?.componentProps?.sumOf !== 'undefined' ? '0.00' : undefined)
                            }),
                            {}
                        );

                        accCopy = {
                            ...accCopy,
                            ...childrenValueMap
                        };
                    } else if (item.isModule && !item.isValueTable) {
                        accCopy[item.key] = Object.entries(data[item.id]).map(([key, value]) => ({
                            ...(value as Record<string, any>),
                            id: key
                        }));
                    } else {
                        accCopy[item.key] = data[item.id];
                    }
                });

                return accCopy;
            }, {} as Record<string, any>);
        }, [sections]);

        const reduceManifestValidation = React.useCallback(
            (values: Record<string, any>, ancillaryData: Record<string, any>, manifest: IManifestFieldHydrated[]) =>
                manifest.reduce((acc, curr) => {
                    if (!Object.keys(curr).length || disableValidation) return acc;
                    return typeof curr.validator !== 'undefined' ||
                        determineRequired({
                            required: curr?.componentProps?.required,
                            values,
                            ancillaryData
                        })
                        ? {
                              ...acc,
                              [curr.key]: ((val: any) => {
                                  if (
                                      curr?.dependentOn &&
                                      !Object.entries(curr.dependentOn).every(([key, dependentValue]) => {
                                          return typeof dependentValue === 'function'
                                              ? dependentValue(values[key])
                                              : values[key] === dependentValue;
                                      })
                                  ) {
                                      return null;
                                  }
                                  if (val === '' || typeof val === 'undefined' || (Array.isArray(val) && !val.length)) {
                                      if (
                                          determineRequired({
                                              required: curr?.componentProps?.required,
                                              values,
                                              ancillaryData
                                          })
                                      ) {
                                          return t('required', { ns: 'common' });
                                      } else {
                                          return null;
                                      }
                                  }
                                  const error = curr.validator?.(val, values);

                                  if (error) {
                                      return typeof error === 'string'
                                          ? t(error, { ns: 'questions' })
                                          : Object.entries(error).reduce(
                                                (acc, [key, value]) => ({
                                                    ...acc,
                                                    [key]: value ? t(value, { ns: 'questions' }) : null
                                                }),
                                                {}
                                            );
                                  }

                                  return null;
                              })(values[curr.key])
                          }
                        : acc;
                }, {}),
            [t, disableValidation]
        );
        const form = useForm({
            // validateInputOnBlur: true,
            // clearInputErrorOnChange: false,
            // validateInputOnChange: true,
            initialValues: initialValues,
            validate: (values: Record<string, any>) => {
                if (progressiveForm) {
                    return reduceManifestValidation(values, ancillaryData, sections[activeStep].manifest);
                } else {
                    return sections.reduce(
                        (acc, curr) => ({
                            ...acc,
                            ...reduceManifestValidation(values, ancillaryData, curr.manifest)
                        }),
                        {}
                    );
                }
            }
        });
        const highlights = React.useMemo(() => {
            if (highlightState && highlightState.length) {
                return highlightState.filter((questionName: string) => {
                    return !form.isDirty(questionName);
                });
            }
        }, [highlightState, form?.values, setHightlightState]);
        React.useImperativeHandle(
            ref,
            () => ({
                isDirty: () => {
                    return form.isDirty();
                },
                reset: () => {
                    form.reset();
                },
                setIsLoading: setIsLoading,
                isLoading: () => {
                    return isLoading;
                }
            }),
            [form, setIsLoading, isLoading]
        );

        const transformFieldNamesToManifestKeys = React.useCallback(
            (values: typeof form.values) => {
                return Object.entries(values).reduce((acc, [key, value]) => {
                    const fieldRef = getStaticKeyByName(key);

                    return {
                        ...acc,
                        [fieldRef]:
                            typeof componentOnSubmitValueTransforms[manifest[fieldRef]?.component] === 'function'
                                ? componentOnSubmitValueTransforms[manifest[fieldRef]?.component](value)
                                : value
                    };
                }, {});
            },
            [getStaticKeyByName]
        );

        const clearStateForSkippedSteps = React.useCallback(
            (fromIndex: number, toIndex: number) => {
                const skippedSections = sections.slice(fromIndex + 1, toIndex);

                const staticKeysToClear = skippedSections.reduce<string[]>((acc, section) => {
                    return [
                        ...acc,
                        ...(section?.manifest ?? [])
                            .filter(({ alwaysCompute }) => !alwaysCompute)
                            .map(({ id }) => getNameByStaticKey(id))
                    ];
                }, []);

                form.setValues(
                    staticKeysToClear.reduce(
                        (acc, curr) => ({
                            ...acc,
                            [curr]: manifest[getStaticKeyByName(curr)]?.defaultValue ?? undefined
                        }),
                        {}
                    )
                );
            },
            [form, sections]
        );

        const scrollToError = (errors: Record<string, any>): void => {
            const topErrorName = Object.keys(errors ?? {})[0];

            if (topErrorName) {
                document
                    .querySelector(`#${getFieldId(topErrorName)}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        };

        const getRevisedSections = React.useCallback(
            ({
                surveySections,
                isReviewStep
            }: {
                surveySections: IFormSection<IManifestFieldHydrated>[];
                isReviewStep?: boolean;
            }) => {
                const activeSections = surveySections.reduce<number[]>((acc, curr, index) => {
                    const skipSection =
                        (!isReviewStep && curr?.reviewStepOnly) ||
                        Object.entries(curr?.skip ?? {}).some(([key, value]) => {
                            if (key === 'ancillaryData' && typeof value === 'function') {
                                return (value as (data: Record<string, any>) => boolean)(ancillaryData);
                            } else {
                                if (key === 'ASP0037') {
                                    console.log(
                                        'skip section?',
                                        (value as any[])
                                            .map((condition) => (condition === 'undefined' ? undefined : condition))
                                            .includes(form?.values?.[getNameByStaticKey(key)])
                                    );
                                }
                                return (value as any[])
                                    .map((condition) => (condition === 'undefined' ? undefined : condition))
                                    .includes(form?.values?.[getNameByStaticKey(key)]);
                            }
                        });

                    return skipSection ? acc : [...acc, index];
                }, []);

                return showReviewStep ? [...activeSections, surveySections.length] : activeSections;
            },
            [showReviewStep, ancillaryData, form?.values, getNameByStaticKey]
        );

        const revisedSections = React.useMemo(() => {
            return getRevisedSections({
                surveySections: sections
            });
        }, [form?.values, sections, getRevisedSections]);

        const onFinish = async () => {
            await onComplete?.();
            removeHighlightState();
            removeActive();
        };

        const onSubmit = React.useCallback(() => {
            return onFinish();
        }, [onFinish]);

        const isFinalStep = React.useMemo(
            () => (progressiveForm ? activeStep === Math.max(...revisedSections) : true),
            [progressiveForm, activeStep, revisedSections]
        );
        const nextStep = React.useCallback(
            (providedVals?: Record<string, any>) => {
                return new Promise<void>((resolve, reject) => {
                    form.onSubmit(
                        async (formVals: Record<string, any>) => {
                            const values = providedVals ?? formVals;
                            setIsLoading(true);

                            try {
                                if (form.validate().hasErrors) {
                                    scrollToError(form.errors);
                                    return;
                                }
                                await onSave?.({
                                    values: transformFieldNamesToManifestKeys(values),
                                    furthestStep:
                                        changedFieldWithDependents === true
                                            ? activeStep
                                            : Math.max(...[furthestStep, activeStep + 1])
                                });
                                if (preventStepProgression) return;
                                if (highlights) setHightlightState(highlights);
                                if (changedFieldWithDependents === true) {
                                    openNewDependenciesModal({
                                        confirmCallback: () => {
                                            setChangedFieldWithDependents(false);
                                        }
                                    });
                                }
                                setIsLoading(false);
                                if (isFinalStep && showReviewStep) {
                                    if (reviewAnswers) {
                                        setActiveStep((current) => {
                                            scrollIntoView({
                                                alignment: 'start'
                                            });
                                            return current + 1;
                                        });
                                    } else {
                                        onFinish();
                                    }
                                } else {
                                    setActiveStep((current) => {
                                        scrollIntoView({
                                            alignment: 'start'
                                        });
                                        if (current < Math.max(...revisedSections)) {
                                            const currRevisedIndex = revisedSections.findIndex(
                                                (val) => val === current
                                            );
                                            const nextIndex = revisedSections[currRevisedIndex + 1];

                                            clearStateForSkippedSteps(current, nextIndex);

                                            return nextIndex;
                                        } else {
                                            return current;
                                        }
                                    });
                                }
                                resolve();
                            } catch (e) {
                                setIsLoading(false);
                                // Sentry log?
                                console.error(e);
                                reject();
                            }
                        },
                        (validationErrors, _values, _event) => {
                            scrollToError(validationErrors);
                            reject();
                        }
                    )();
                });
            },
            [sections, activeStep, setActiveStep, form, revisedSections, isFinalStep, highlights, setHightlightState]
        );

        const prevStep = () =>
            setActiveStep((current) => {
                if (activeStep > Math.min(...revisedSections)) {
                    const currIndex = revisedSections.findIndex((val) => val === current);

                    return revisedSections[currIndex - 1];
                } else {
                    return current;
                }
            });

        const fieldsWithDependencies = React.useMemo<Record<string, Record<string, any>>>(() => {
            return sections.reduce((acc, { manifest = [] }) => {
                manifest.forEach((item) => {
                    if (item?.dependentOn) acc[item.key] = item?.dependentOn;
                });

                return acc;
            }, {} as Record<string, any>);
        }, [sections]);
        const recalculateField = ({
            recalcKey,
            originKey,
            originKeyNewVal
        }: {
            recalcKey: string;
            originKey: string;
            originKeyNewVal: any;
        }) => {
            if (typeof manifest?.[recalcKey]?.calculateValue !== 'function') return;
            const formPropsRecalc = form.getInputProps(getNameByStaticKey(recalcKey));
            const newVal = manifest?.[recalcKey]?.calculateValue?.({
                ...convertMapFromNamesToKeys(form.values),
                [getStaticKeyByName(originKey)]: originKeyNewVal
            });

            formPropsRecalc.onChange(newVal);
        };
        const conditionalFields = React.useMemo<Record<string, boolean>>(() => {
            const updateFields: Record<string, any> = {};
            const fields = Object.entries(fieldsWithDependencies).reduce((acc, [key, value]) => {
                const showField = Object.entries(value).every(([depKey, depVal]) => {
                    const state = depKey === 'ancillaryData' ? ancillaryData : form?.values?.[depKey];

                    return typeof depVal === 'function' ? depVal(state) : state === depVal;
                });

                // Should this set to undefined?
                // if (!showField && form.values[key] !== initialValues[key]) {
                //     updateFields[key] = initialValues[key];
                // }
                if (!showField && form.values[key] !== undefined) {
                    if (highlightState && highlightState?.includes(key)) {
                        setHightlightState(highlightState.splice(highlightState.indexOf(key), 1));
                    }
                    updateFields[key] = undefined;
                } else if (
                    showField &&
                    form.values[key] === undefined &&
                    typeof manifest[getStaticKeyByName(key)]?.defaultValue !== 'undefined'
                ) {
                    updateFields[key] = manifest[getStaticKeyByName(key)]?.defaultValue;
                }

                return {
                    ...acc,
                    [key]: showField
                };
            }, {});

            if (Object.keys(updateFields).length) form.setValues(updateFields);

            return fields;
        }, [form?.values, fieldsWithDependencies]);

        const FormInputs = React.useMemo<Record<string, any>>(
            () => ({
                DatePickerInput,
                MonthPickerInput,
                NumericFormatInput,
                InputValueTable,
                NumberInput,
                WageRanger,
                NumberOrNoAnswerRadioGroup,
                ListField,
                PatternNumberInput,
                PhoneNumberGroup,
                Select,
                MultiSelect,
                Textarea,
                TextInput,
                YearPickerInput,
                YearMonthPickerInput,
                PercentageOwnershipInput,
                YesNoRadio
            }),
            [
                DatePickerInput,
                MonthPickerInput,
                NumericFormatInput,
                InputValueTable,
                NumberInput,
                WageRanger,
                NumberOrNoAnswerRadioGroup,
                ListField,
                PatternNumberInput,
                PhoneNumberGroup,
                Select,
                MultiSelect,
                Textarea,
                TextInput,
                YearPickerInput,
                YearMonthPickerInput,
                PercentageOwnershipInput,
                YesNoRadio
            ]
        );
        const FormModules = React.useMemo<Record<string, any>>(
            () => ({
                PrimaryContactForm,
                ContactsModule,
                ConfirmContactsModule,
                FinancialsTable,
                DefaultSubForm
            }),
            [PrimaryContactForm, ContactsModule, ConfirmContactsModule, FinancialsTable, DefaultSubForm]
        );

        const GetFormInput = (field: IManifestFieldHydrated, index: number) => {
            if (!Object.keys(field).length) {
                return (
                    <GridCol span={12} key={index}>
                        <Divider my="sm" variant="dotted" />
                    </GridCol>
                );
            }

            const { component, componentProps, hidden, gridSpan, permission, ...inputProps } = field;
            if (hidden === true || (Array.isArray(hidden) && userType && hidden.includes(userType))) {
                return null;
            }

            const {
                editable,
                disabled,
                key,
                validator,
                dataSetKey,
                controlledDataSet,
                ancillaryDataKey,
                writePermission,
                ...restOfInputProps
            } = inputProps;
            let additionalComponentsProps = {};

            if (dataSetKey) {
                additionalComponentsProps = {
                    ...additionalComponentsProps,
                    data: getTranslatedDataSet(dataSetKey)
                };
            } else if (controlledDataSet) {
                additionalComponentsProps = {
                    ...additionalComponentsProps,
                    data: controlledDataSet?.transform
                        ? controlledDataSet.transform?.(ancillaryData[controlledDataSet.key], form?.values)
                        : ancillaryData[controlledDataSet.key]
                };
            } else if (ancillaryDataKey) {
                additionalComponentsProps = {
                    ...additionalComponentsProps,
                    data: ancillaryData[ancillaryDataKey]
                };
            }

            if (!editMode) {
                additionalComponentsProps = {
                    ...additionalComponentsProps,
                    placeholder: '--'
                };
            }
            const formProps = form.getInputProps(key);
            const InputComponent = React.createElement(FormInputs[component] || 'div', {
                prefix: key,
                readOnly: !editMode,
                disabled:
                    determineDisabled({ disabled, values: form.values, ancillaryData }) ||
                    (editMode && permission ? permission !== 'write' : false),
                key,
                parentform: form,
                targetperiod: targetPeriod,
                writepermission: writePermission,
                highlight: (highlights?.includes(key) ?? false).toString(),
                ...componentProps,
                computed: componentProps?.computed === true ? 'true' : 'false',
                required: determineRequired({
                    required: componentProps?.required,
                    values: form.values,
                    ancillaryData
                }),
                ...additionalComponentsProps,
                ...formProps,
                onChange: questionsWithDependents?.[key]
                    ? (val: any) => {
                          if (furthestStep > activeStep) {
                              setChangedFieldWithDependents(true);
                          }
                          formProps.onChange(val);
                          if (field.recalculateKeysOnChange && field.recalculateKeysOnChange.length) {
                              field.recalculateKeysOnChange.forEach((recalcKey) => {
                                  recalculateField({
                                      recalcKey,
                                      originKey: key,
                                      originKeyNewVal: val
                                  });
                              });
                          }
                      }
                    : (val: any) => {
                          formProps.onChange(val);
                          if (field.recalculateKeysOnChange && field.recalculateKeysOnChange.length) {
                              field.recalculateKeysOnChange.forEach((recalcKey) => {
                                  recalculateField({
                                      recalcKey,
                                      originKey: key,
                                      originKeyNewVal: val
                                  });
                              });
                          }
                      }
            });

            return (
                <GridCol md={gridSpan} key={index}>
                    <div id={getFieldId(key)}>{InputComponent}</div>
                </GridCol>
            );
        };

        const GetFormModule = (field: IManifestFieldHydrated, index: number) => {
            const { component, hidden, ...props } = field;
            let additionalComponentsProps = {};
            if (hidden === true) {
                return null;
            }
            const { key, controlledDataSet, subManifest } = props;

            if (controlledDataSet) {
                additionalComponentsProps = {
                    ...additionalComponentsProps,
                    ancillaryData: {
                        ...ancillaryData,
                        parentFormState: form.values
                    },
                    data: controlledDataSet?.transform
                        ? controlledDataSet.transform?.(ancillaryData[controlledDataSet.key], form?.values)
                        : ancillaryData[controlledDataSet.key]
                };
            }

            const InputComponent = React.createElement<typeof form.values>(FormModules[component] || 'div', {
                parentform: form,
                readOnly: !editMode,
                hidden: hidden === 'readonly' ? !editMode : false,
                parentNext: nextStep,
                parentPrev: activeStep !== 0 ? prevStep : undefined,
                activeStep: activeStep,
                targetperiod: targetPeriod,
                trigger,
                formId,
                clientId,
                subManifest,
                ...additionalComponentsProps,
                ...props
            });

            return (
                <GridCol span={12} key={index}>
                    <div id={getFieldId(key)}>{InputComponent}</div>
                </GridCol>
            );
        };

        const questionConfigToField = (questionConfig: IManifestFieldHydrated, index: number) => {
            const isIrrelevant = conditionalFields[questionConfig.key] === false;

            return questionConfig?.isModule
                ? GetFormModule(questionConfig, index)
                : GetFormInput(
                      Object.keys(questionConfig).length
                          ? {
                                ...questionConfig,
                                hidden:
                                    ((questionConfig?.hideIfIrrelevant && isIrrelevant) || questionConfig.hidden) ??
                                    false,
                                disabled: isIrrelevant || questionConfig.disabled
                            }
                          : questionConfig,
                      index
                  );
        };

        const fieldElements = React.useMemo(() => {
            if (progressiveForm) {
                return sections[activeStep]?.manifest?.map(questionConfigToField).filter(Boolean);
            } else {
                return sections
                    .map((section) => {
                        return section.manifest?.map(questionConfigToField).filter(Boolean);
                    })
                    .filter(Boolean);
            }
        }, [sections, activeStep, GetFormInput, conditionalFields, progressiveForm, highlights]);

        // Rendered field errors will not change to current language if global lang settings changed
        // without this callback
        i18n.on('languageChanged', () => {
            if (form.errors && Object.keys(form.errors).length) {
                form.clearErrors();
                form.validate();
            }
        });
        const hideActionButtons = React.useMemo(() => {
            return (
                progressiveForm &&
                sections?.[activeStep]?.manifest.length === 1 &&
                (typeof sections?.[activeStep]?.manifest[0]?.targetChildId !== 'undefined' ||
                    typeof sections?.[activeStep]?.manifest[0]?.targetStateId !== 'undefined' ||
                    ComponentsWithOwnActionButtons.includes(sections?.[activeStep]?.manifest[0]?.id))
            );
        }, [progressiveForm, sections, activeStep]);

        const openNewDependenciesModal = React.useCallback(({ confirmCallback }: { confirmCallback: () => void }) => {
            openConfirmModal({
                title: t('resetProgress.title', { ns: 'notifications' }),
                children: <Text size="sm">{t('resetProgress.message', { ns: 'notifications' })}</Text>,
                labels: {
                    confirm: t('ok', { ns: 'common' }),
                    cancel: t('cancel', { ns: 'common' })
                },
                cancelProps: { style: { display: 'none' } },
                onConfirm: () => {
                    confirmCallback();
                }
            });
        }, []);

        if (reviewOnly) {
            return (
                <FormSummary
                    readOnly
                    onHighlightQuestion={onHighlightQuestion}
                    highlightedQuestions={
                        (surveyStatus === SurveyStatus.SUBMITTED ? highlightedQuestions : highlights) ?? []
                    }
                    answers={form.values}
                    {...{
                        sections,
                        ancillaryData,
                        conditionalFields,
                        setActiveStep
                    }}
                    revisedSections={getRevisedSections({
                        surveySections: sections,
                        isReviewStep: true
                    })}
                    targetperiod={targetPeriod}
                />
            );
        }

        const isSectionDirty = React.useMemo(() => {
            if (progressiveForm && sections?.[activeStep]) {
                return sections?.[activeStep]?.manifest.some(({ key }) => {
                    return form.isDirty(key);
                });
            } else {
                return false;
            }
        }, [sections, activeStep, progressiveForm, form]);

        return (
            // noValidate attribute on form element to disable native validation on required fields
            <FormWrapper {...{ targetRef, isSubForm }}>
                {/* {progressiveForm && sections.length > 1 ? (
                    <Progress value={activeStep * (100 / sections.length)} />
                ) : null} */}
                <Group align="flex-start" className={classes.gridGroup}>
                    {progressiveForm && sections.length > 1 ? (
                        <Box className={classes.stepperBox}>
                            <FormStepper
                                orientation="vertical"
                                active={activeStep}
                                showReviewStep={showReviewStep}
                                onStepClick={setActiveStep}
                                steps={sectionsToSteps(sections, t)}
                                allowNavigationForwardToStep={furthestStep}
                                highlightedQuestions={highlights}
                                isSectionDirty={isSectionDirty}
                                discardChanges={form.reset}
                            />
                        </Box>
                    ) : null}

                    <Box
                        className={cx(classes.formBox, {
                            [classes.formBoxWithStepper]: progressiveForm && sections.length > 1
                        })}
                    >
                        {progressiveForm ? (
                            <>
                                {/* {sections?.[activeStep]?.name ? (
                                    <Title order={3}>
                                        <InterpolatedFormCopy copy={sections[activeStep].name} {...{ data }} />
                                    </Title>
                                ) : null} */}
                                {activeStep > sections.length - 1 ? (
                                    <FormSummary
                                        answers={form.values}
                                        {...{
                                            sections,
                                            ancillaryData,
                                            conditionalFields,
                                            setActiveStep
                                        }}
                                        revisedSections={getRevisedSections({
                                            surveySections: sections,
                                            isReviewStep: true
                                        })}
                                        highlightedQuestions={highlights}
                                        targetperiod={targetPeriod}
                                    />
                                ) : (
                                    <>
                                        {sections?.[activeStep]?.name &&
                                        sections?.[activeStep]?.showNameAsSectionHeader ? (
                                            <Title order={4} mb={'xs'}>
                                                <InterpolatedFormCopy copy={sections[activeStep].name} {...{ data }} />
                                            </Title>
                                        ) : null}
                                        {sections?.[activeStep]?.description ? (
                                            <Text mb="md" color="dimmed" className={classes.sectionDescription}>
                                                <InterpolatedFormCopy
                                                    copy={
                                                        i18next.exists(
                                                            `surveys:sectionDescriptions.${sections[activeStep].description}`
                                                        )
                                                            ? t(
                                                                  `sectionDescriptions.${sections[activeStep].description}`,
                                                                  {
                                                                      ns: 'surveys'
                                                                  }
                                                              )
                                                            : sections[activeStep].description ?? ''
                                                    }
                                                    {...{ data }}
                                                    targetperiod={targetPeriod}
                                                />
                                            </Text>
                                        ) : null}
                                        <Grid className={classes.grid} grow>
                                            {fieldElements}
                                        </Grid>
                                    </>
                                )}
                            </>
                        ) : (
                            sections.map((section, index) => (
                                <Box
                                    key={index}
                                    className={cx(classes.formSection, {
                                        [classes.noMarginTop]: index === 0,
                                        [classes.drawerCardStyle]: drawerCard,
                                        [classes.noCardStyle]: noCardStyle,
                                        [classes.confirmContact]: confirmContact,
                                        [classes.formInsertCardPadding]: formInsert
                                    })}
                                    {...(section?.sectionProps ? section.sectionProps : {})}
                                >
                                    {section.name ? (
                                        <Title order={3} mb={section?.description ? 'xs' : 'xl'}>
                                            <InterpolatedFormCopy
                                                copy={section.name}
                                                {...{ data }}
                                                targetperiod={targetPeriod}
                                            />
                                        </Title>
                                    ) : null}
                                    {section?.description ? (
                                        <Text mb="xl" color="dimmed" className={classes.sectionDescription}>
                                            <InterpolatedFormCopy
                                                copy={section.description}
                                                {...{ data }}
                                                targetperiod={targetPeriod}
                                            />
                                        </Text>
                                    ) : null}
                                    <Grid className={classes.grid} grow>
                                        {fieldElements[index]}
                                    </Grid>
                                </Box>
                            ))
                        )}
                        {editMode && !hideActionButtons ? (
                            <Group
                                style={{ flexDirection: 'row-reverse' }}
                                position="apart"
                                mt="lg"
                                className={cx('', {
                                    [classes.formInsert]: formInsert
                                })}
                            >
                                <Group>
                                    {progressiveForm && canSubmit && activeStep > sections.length - 1 ? (
                                        <Button onClick={onSubmit} loading={isLoading}>
                                            {t('saveAndSubmit', {
                                                ns: 'common'
                                            })}
                                        </Button>
                                    ) : (
                                        <>
                                            {onCancel && !hideCancel ? (
                                                <Button
                                                    variant="default"
                                                    onClick={() => onCancel(form)}
                                                    disabled={isLoading}
                                                >
                                                    {t('Cancel', {
                                                        ns: 'common'
                                                    })}
                                                </Button>
                                            ) : null}
                                            {activeStep !== 0 && !hideDefaultBack ? (
                                                <Button variant="default" onClick={prevStep} disabled={isLoading}>
                                                    {t('back', {
                                                        ns: 'common'
                                                    })}
                                                </Button>
                                            ) : null}
                                            {parentPrev ? (
                                                <Button variant="default" onClick={parentPrev} disabled={isLoading}>
                                                    {t('back', {
                                                        ns: 'common'
                                                    })}
                                                </Button>
                                            ) : null}
                                            {!progressiveForm || !isFinalStep || (isFinalStep && showReviewStep) ? (
                                                <Button
                                                    onClick={() => {
                                                        nextStep();
                                                    }}
                                                    disabled={
                                                        isLoading ||
                                                        externalIsLoading ||
                                                        (dirtySaveOnly
                                                            ? sections.length === 1 && !form.isDirty()
                                                            : false)
                                                    }
                                                    loading={isLoading || externalIsLoading}
                                                >
                                                    {saveLabelKey
                                                        ? t(saveLabelKey, { ns: 'common' })
                                                        : t(
                                                              !showReviewStep && activeStep === sections.length - 1
                                                                  ? 'saveFinish'
                                                                  : 'saveContinue',
                                                              {
                                                                  ns: 'common'
                                                              }
                                                          )}
                                                </Button>
                                            ) : null}
                                        </>
                                    )}
                                </Group>
                                {leftActionButtons ? <div>{leftActionButtons}</div> : null}
                            </Group>
                        ) : null}
                    </Box>
                </Group>
            </FormWrapper>
        );
    }
);
